import { useMemo } from 'react'

import { useGetOrganizationUuid } from 'common'
import { getDashboardEventIdForInvitees } from 'components/meetings/utils/get_dashboard_event_id_for_invitees'
import { useGetMeetingSupergraphQuery } from 'shared/api/meetings'

import { useGetMeetingQuery } from '../api'
import { normalizeEventGuestEmails } from '../normalize_event_guest_emails'
import { mapNodeToMeeting } from '../utils'

export const usePrimaryCalendlyMeetingDetail = ({
  shouldUseSupergraphMeetingDetail,
  shouldFetchMeetingDetailInProvider,
  eventId,
  selectedView,
  isCalendlyMeeting,
  detailPollingConfig,
  cacheMeetingData,
  timezone,
  hasMsTeamsFlag,
}) => {
  const organizationUuid = useGetOrganizationUuid()
  const shouldFetchSupergraphApi = Boolean(shouldFetchMeetingDetailInProvider && shouldUseSupergraphMeetingDetail)
  const shouldCallScheduledEventsRest = Boolean(
    shouldFetchMeetingDetailInProvider && isCalendlyMeeting
  )

  // Single-event GET by uuid: never pass meetings list scope (user/team/all_users_and_teams, etc.).
  // Supergraph path uses REST only to enrich publisher fields; legacy path uses REST as primary
  // detail but the same scheduled_events endpoint must not receive org-wide scope_type queries.
  const restMeetingQueryArgs = useMemo(() => {
    if (!shouldCallScheduledEventsRest) {
      return { eventId: undefined, selectedView: undefined, scopeType: undefined, scopeId: undefined }
    }
    return { eventId, selectedView, scopeType: undefined, scopeId: undefined }
  }, [shouldCallScheduledEventsRest, eventId, selectedView])

  const {
    data: restMeetingData,
    isError: restApiError,
    isSuccess: restApiSuccess,
    error: restApiErrorPayload,
  } = useGetMeetingQuery(restMeetingQueryArgs, {
    skip: !shouldCallScheduledEventsRest,
    refetchOnMountOrArgChange: true,
    ...detailPollingConfig,
  })

  const {
    data: supergraphMeetingData,
    isError: supergraphError,
    isSuccess: supergraphSuccess,
    error: supergraphErrorPayload,
  } = useGetMeetingSupergraphQuery(
    { id: eventId, organizationUuid: organizationUuid || '' },
    {
      skip: !shouldFetchSupergraphApi || !eventId || !organizationUuid,
      refetchOnMountOrArgChange: true,
      ...detailPollingConfig,
    }
  )

  const primaryMeetingData = useMemo(() => {
    if (!shouldUseSupergraphMeetingDetail) {
      if (!restMeetingData) return undefined
      // RTK can briefly retain the previous subscription's payload when `eventId` changes; do not
      // merge a mismatched meeting into provider state (avoids phantom detail + merge flicker).
      if (restMeetingData.uuid != null && restMeetingData.uuid !== eventId) return undefined
      return {
        ...restMeetingData,
        eventGuestEmails: normalizeEventGuestEmails(restMeetingData),
      }
    }
    if (!supergraphMeetingData?.uuid || supergraphMeetingData.uuid !== eventId) {
      return undefined
    }
    let mapped = mapNodeToMeeting(supergraphMeetingData, timezone || 'UTC', hasMsTeamsFlag)
    // Same RTK stale-payload case as the REST-only branch above: supergraph can match the new
    // `eventId` while `useGetMeetingQuery` still briefly returns the previous meeting.
    const shouldMergeRestEnrichment = restMeetingData
      && (restMeetingData.uuid == null || restMeetingData.uuid === eventId)
    if (shouldMergeRestEnrichment) {
      const dash = getDashboardEventIdForInvitees(restMeetingData)
      mapped = { ...mapped, inviteesDashEventId: dash }
      const isNumericDash = typeof dash === 'number'
        || (typeof dash === 'string' && /^\d+$/.test(dash) && dash.length < 20)
      if (isNumericDash) {
        mapped = { ...mapped, id: dash }
      }
      // Supergraph `getMeetingById` does not query guest emails; REST scheduled-events detail does.
      const fromRest = normalizeEventGuestEmails(restMeetingData)
      const fromMapped = normalizeEventGuestEmails(mapped)
      if (fromRest.length > 0 && fromMapped.length === 0) {
        mapped = { ...mapped, eventGuestEmails: fromRest }
      }
    }
    const cacheId = cacheMeetingData?.id
    if (cacheId != null && cacheId !== '' && (mapped.id == null || mapped.id === '')) {
      mapped = { ...mapped, id: cacheId }
    }
    const cacheMi = cacheMeetingData?.meetingIntelligence
    const detailMi = mapped.meetingIntelligence
    if (cacheMi && detailMi && typeof cacheMi === 'object' && typeof detailMi === 'object') {
      return {
        ...mapped,
        meetingIntelligence: { ...cacheMi, ...detailMi },
      }
    }
    return mapped
  }, [
    shouldUseSupergraphMeetingDetail,
    supergraphMeetingData,
    restMeetingData,
    cacheMeetingData,
    eventId,
    timezone,
    hasMsTeamsFlag,
  ])

  return {
    primaryMeetingData,
    primaryApiError: shouldUseSupergraphMeetingDetail ? supergraphError : restApiError,
    primaryApiSuccess: shouldUseSupergraphMeetingDetail ? supergraphSuccess : restApiSuccess,
    primaryApiErrorPayload: shouldUseSupergraphMeetingDetail ? supergraphErrorPayload : restApiErrorPayload,
  }
}
